import { formatPeriod } from './piFormat.js';
import {
  JOURNEY_OPTIONS, MAX_PAGE_SIZE, MAX_RANGE_DAYS, SIZE_BAND_OPTIONS, addDays, buildApiQuery, compareEnabled, optionLabel, parseIsoDate,
  previousPeriod, rangeError, todayIso,
} from './piQuery.js';

export const SALE_PRESETS = [
  { value: '3', months: 3, label: '3 meses' },
  { value: '6', months: 6, label: '6 meses' },
  { value: '12', months: 12, label: '12 meses' },
];

export const DEFAULT_SALE_PRESET = '6';

export const MAX_SALE_RANGE_DAYS = MAX_RANGE_DAYS;

export const COHORT_OPTIONS = [
  { value: 'month', label: 'Mês' },
  { value: 'quarter', label: 'Trimestre' },
];

export const UNIVERSE_OPTIONS = [
  { value: 'sold', label: 'Vendidas' },
  { value: 'all', label: 'Todas' },
  { value: 'unsold', label: 'Não vendidas' },
];

export const STATUS_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'active', label: 'Ativas' },
  { value: 'churned', label: 'Canceladas ou excluídas' },
  { value: 'unpaid', label: 'Vendida sem pagar (ainda trial)' },
];

export const TRIAL_STATUS_LABEL = 'Em trial';

export const statusOptionsFor = (universe) => STATUS_OPTIONS.map((option) => (option.value === 'unpaid' && (universe === 'all' || universe === 'unsold')
  ? { value: option.value, label: TRIAL_STATUS_LABEL }
  : option));

export const TRIAL_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'with', label: 'Com trial' },
  { value: 'without', label: 'Venda direta' },
];

export const PLAN_OPTIONS = [
  { value: '0', label: 'Trial' },
  { value: '1', label: 'Free' },
  { value: '2', label: 'Até 10' },
  { value: '3', label: 'Acima de 10' },
];

export const ORIGIN_OPTIONS = [
  { value: 'master', label: 'Master' },
  { value: 'landing_page', label: 'Landing page' },
];

export const ONBOARDING_FILTER_KEYS = [
  'saleFrom', 'saleTo', 'salePreset', 'cohort', 'universe', 'status', 'trial', 'started', 'plan', 'journeyNow', 'origin', 'shard',
];

export const ONBOARDING_ENDPOINTS = {
  summary: ['period', 'compare', 'universe', 'status', 'trial', 'started', 'plan', 'sizeBand', 'segment', 'journey', 'origin', 'shard'],
  cohorts: ['period', 'universe', 'status', 'trial', 'started', 'plan', 'sizeBand', 'segment', 'journey', 'origin', 'shard', 'granularity'],
  companies: ['period', 'universe', 'status', 'trial', 'started', 'plan', 'sizeBand', 'segment', 'journey', 'origin', 'shard'],
  stuck: ['sizeBand', 'segment', 'journey', 'plan', 'origin', 'shard'],
};

export const ONBOARDING_SORTS = {
  companies: ['name', 'soldAt', 'daysSinceSale', 'employees', 'overdueDays', 'stage'],
  stuck: ['overdueDays', 'daysSinceSale', 'name', 'lastProgress'],
};

export const WINDOW_VALUES = [15, 30, 60];

const DAY_MS = 86400000;
const ORIGIN = /^[0-9a-z_.-]{1,20}$/;
const SHARD = /^[0-9A-Za-z]{1,8}$/;
const KEY = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;

const text = (value) => {
  const raw = Array.isArray(value) ? value[0] : value;
  return raw === null || raw === undefined ? '' : String(raw).trim();
};

const has = (list, value) => list.indexOf(value) !== -1;

const allowed = (options, value) => options.some((option) => option.value === value);

const pad2 = (value) => (value < 10 ? '0' : '') + value;

const presetOf = (value) => SALE_PRESETS.filter((preset) => preset.value === value)[0] || null;

export const addMonths = (iso, months) => {
  const date = parseIsoDate(iso);
  if (!date) return '';
  const year = date.getFullYear();
  const month = date.getMonth() + months;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const shifted = new Date(Date.UTC(year, month, Math.min(date.getDate(), lastDay)));
  return shifted.getUTCFullYear() + '-' + pad2(shifted.getUTCMonth() + 1) + '-' + pad2(shifted.getUTCDate());
};

const presetSalePeriod = (preset, today) => {
  const to = addDays(today, -1);
  const from = addDays(addMonths(to, -preset.months), 1);
  const days = Math.round((Date.parse(to + 'T00:00:00Z') - Date.parse(from + 'T00:00:00Z')) / DAY_MS) + 1;
  return { from: from, to: to, days: days, preset: preset.value, months: preset.months, custom: false };
};

export const resolveSalePeriod = (filters, now) => {
  const source = filters || {};
  const today = todayIso(now);
  const fallback = presetSalePeriod(presetOf(text(source.salePreset)) || presetOf(DEFAULT_SALE_PRESET), today);
  const from = text(source.saleFrom);
  const to = text(source.saleTo);
  if (!from && !to) return Object.assign({ error: null }, fallback);
  const error = rangeError(from, to, today);
  if (error) return Object.assign({ error: error }, fallback);
  const days = Math.round((Date.parse(to + 'T00:00:00Z') - Date.parse(from + 'T00:00:00Z')) / DAY_MS) + 1;
  return { from: from, to: to, days: days, preset: 'custom', months: null, custom: true, error: null };
};

export const previousSalePeriod = (period) => previousPeriod(period);

export const salePeriodText = (period) => {
  if (!period || !period.from) return '';
  return formatPeriod(period.from, period.to) + ' (' + period.days + ' dias)';
};

export const applySalePreset = (filters, preset) => Object.assign({}, filters, {
  salePreset: preset === DEFAULT_SALE_PRESET || !presetOf(preset) ? '' : preset,
  saleFrom: '',
  saleTo: '',
});

export const applySaleRange = (filters, from, to) => Object.assign({}, filters, { salePreset: '', saleFrom: text(from), saleTo: text(to) });

export const cleanOnboardingFilters = (filters) => {
  const source = filters || {};
  const preset = text(source.salePreset);
  const universe = text(source.universe);
  const status = text(source.status);
  const trial = text(source.trial);
  const plan = text(source.plan);
  const journey = text(source.journeyNow);
  const origin = text(source.origin).toLowerCase();
  const shard = text(source.shard);
  return {
    saleFrom: parseIsoDate(text(source.saleFrom)) ? text(source.saleFrom) : '',
    saleTo: parseIsoDate(text(source.saleTo)) ? text(source.saleTo) : '',
    salePreset: presetOf(preset) && preset !== DEFAULT_SALE_PRESET ? preset : '',
    cohort: text(source.cohort) === 'quarter' ? 'quarter' : '',
    universe: universe !== 'sold' && allowed(UNIVERSE_OPTIONS, universe) ? universe : '',
    status: status !== 'all' && allowed(STATUS_OPTIONS, status) ? status : '',
    trial: trial !== 'all' && allowed(TRIAL_OPTIONS, trial) ? trial : '',
    started: has(['1', 'true'], text(source.started)) ? '1' : '',
    plan: allowed(PLAN_OPTIONS, plan) ? plan : '',
    journeyNow: allowed(JOURNEY_OPTIONS, journey) ? journey : '',
    origin: ORIGIN.test(origin) ? origin : '',
    shard: SHARD.test(shard) ? shard.toUpperCase() : '',
  };
};

export const hasOnboardingFilters = (filters) => {
  const clean = cleanOnboardingFilters(filters);
  return ONBOARDING_FILTER_KEYS.some((key) => !!clean[key]);
};

const positive = (value, fallback, max) => {
  const number = Math.floor(Number(value));
  if (!isFinite(number) || number < 1) return fallback;
  return max && number > max ? max : number;
};

export const windowParam = (value) => {
  const number = Number(text(value).replace(/\D/g, ''));
  return has(WINDOW_VALUES, number) ? number : null;
};

export const stageParam = (value) => {
  const raw = text(value);
  if (!/^\d$/.test(raw)) return null;
  const number = Number(raw);
  return number >= 0 && number <= 5 ? number : null;
};

export const onboardingParams = (filters, endpoint, extra, now) => {
  const fields = ONBOARDING_ENDPOINTS[endpoint] || ONBOARDING_ENDPOINTS.summary;
  const clean = cleanOnboardingFilters(filters);
  const params = buildApiQuery(filters, { fields: ['compare', 'sizeBand', 'segment'].filter((field) => has(fields, field)), now: now });
  if (has(fields, 'period')) {
    const period = resolveSalePeriod(clean, now);
    params.from = period.from;
    params.to = period.to;
  }
  if (has(fields, 'universe')) params.universe = clean.universe || 'sold';
  if (has(fields, 'status') && clean.status) params.status = clean.status;
  if (has(fields, 'trial') && clean.trial) params.trial = clean.trial;
  if (has(fields, 'started') && clean.started) params.started = 'true';
  if (has(fields, 'plan') && clean.plan) params.plan = clean.plan;
  if (has(fields, 'journey') && clean.journeyNow) params.journey = clean.journeyNow;
  if (has(fields, 'origin') && clean.origin) params.origin = clean.origin;
  if (has(fields, 'shard') && clean.shard) params.shard = clean.shard;
  if (has(fields, 'granularity')) params.granularity = clean.cohort || 'month';
  const more = extra || {};
  const sorts = ONBOARDING_SORTS[endpoint] || [];
  if (KEY.test(text(more.milestone))) params.milestone = text(more.milestone);
  if (windowParam(more.window) !== null) params.window = String(windowParam(more.window));
  if (more.reached === true || more.reached === false) params.reached = more.reached ? 'true' : 'false';
  if (stageParam(more.stage) !== null) params.stage = String(stageParam(more.stage));
  if (KEY.test(text(more.reason))) params.reason = text(more.reason);
  if (has(sorts, text(more.sort))) {
    params.sort = text(more.sort);
    params.desc = more.desc ? 'true' : 'false';
  }
  if (more.page !== undefined) params.page = String(positive(more.page, 1));
  if (more.pageSize !== undefined) params.pageSize = String(positive(more.pageSize, 25, MAX_PAGE_SIZE));
  return params;
};

export const onboardingFilterLabels = (filters) => {
  const clean = cleanOnboardingFilters(filters);
  const labels = [];
  if (clean.universe) labels.push('Universo: ' + optionLabel(UNIVERSE_OPTIONS, clean.universe));
  if (clean.status) labels.push('Situação hoje: ' + optionLabel(statusOptionsFor(clean.universe), clean.status));
  if (clean.trial) labels.push('Trial antes da venda: ' + optionLabel(TRIAL_OPTIONS, clean.trial));
  if (clean.started) labels.push('Só as que iniciaram o uso');
  if (clean.plan) labels.push('Plano: ' + optionLabel(PLAN_OPTIONS, clean.plan));
  if (clean.journeyNow) labels.push('Jornada: ' + optionLabel(JOURNEY_OPTIONS, clean.journeyNow));
  if (clean.origin) labels.push('Origem: ' + optionLabel(ORIGIN_OPTIONS, clean.origin));
  if (clean.shard) labels.push('Banco: ' + clean.shard);
  return labels;
};

export const stuckIgnoredLabels = (filters) => {
  const clean = cleanOnboardingFilters(filters);
  const labels = [];
  if (clean.universe) labels.push('Universo');
  if (clean.status) labels.push('Situação hoje');
  if (clean.trial) labels.push('Trial antes da venda');
  if (clean.started) labels.push('Só as que iniciaram o uso');
  return labels;
};

export const planLabel = (value) => {
  const key = typeof value === 'number' ? String(value) : text(value);
  return allowed(PLAN_OPTIONS, key) ? optionLabel(PLAN_OPTIONS, key) : '';
};

export const originLabel = (value) => {
  const key = text(value);
  if (!key) return '';
  return allowed(ORIGIN_OPTIONS, key) ? optionLabel(ORIGIN_OPTIONS, key) : key;
};

export const sizeBandLabel = (value) => {
  const key = text(value);
  return key ? optionLabel(SIZE_BAND_OPTIONS, key) : '';
};

export const compareText = (filters, now) => {
  if (!compareEnabled(filters)) return '';
  const previous = previousSalePeriod(resolveSalePeriod(cleanOnboardingFilters(filters), now));
  return previous ? formatPeriod(previous.from, previous.to) : '';
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piOnboardingFilters.js